import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  AppliedDiscountMeta,
  DiscountExclusionReason,
  PaymentPreviewPayableV1Response,
  PaymentPreviewPaymentV1Response,
  PaymentPreviewV1Response,
  SessionDocumentDiscountState,
  SessionDocumentInfo,
  UpdateAppliedDiscountAmountAction,
} from '../models';
import { roundTo2Decimals } from '../helpers';

type DiscountState = { [key: string]: SessionDocumentDiscountState };

@Injectable({
  providedIn: 'root',
})
export class DiscountsHelperService {
  constructor(private translationService: TranslateService) {}

  getAppliedDiscountAmountPayload(
    paymentPreview: PaymentPreviewV1Response,
    selectedDocuments: SessionDocumentInfo[],
    appliedDiscountMeta: AppliedDiscountMeta[],
  ) {
    const payload: UpdateAppliedDiscountAmountAction = {
      selectedDocuments: selectedDocuments.slice(),
      appliedDiscountMetadata: appliedDiscountMeta?.slice() ?? [],
    };

    paymentPreview.payments.forEach(
      (paymentInformation: PaymentPreviewPaymentV1Response) => {
        // filter out payables that do not have discounts at all
        paymentInformation.payment.payables
          .filter((payable) => this.payableFilter(payable))
          .forEach((payable: PaymentPreviewPayableV1Response) => {
            // get payable discount state
            const discountState: SessionDocumentDiscountState =
              this.getPayableDiscountState(payable);

            // build the default applied discount metadata
            const defaultAppliedDiscountMeta: AppliedDiscountMeta =
              this.buildDefaultAppliedDiscountMeta(payable, discountState);

            // update the payable document
            const updatedPayableDocument: SessionDocumentInfo =
              this.updatePayableDocument(
                selectedDocuments,
                defaultAppliedDiscountMeta,
              );

            // update the payable applied discount metadata
            const updatedPayableDiscountMeta: AppliedDiscountMeta =
              this.updatePayableDiscountMeta(
                appliedDiscountMeta,
                defaultAppliedDiscountMeta,
              );

            // update payload with the updated document and discount metadata
            const documentIndex = payload.selectedDocuments.findIndex(
              (x) => x.urn === defaultAppliedDiscountMeta.urn,
            );
            payload.selectedDocuments.splice(
              documentIndex,
              1,
              updatedPayableDocument,
            );

            const discountIndex = payload.appliedDiscountMetadata.findIndex(
              (x) => x.urn === defaultAppliedDiscountMeta.urn,
            );

            if (discountIndex === -1) {
              payload.appliedDiscountMetadata.push(updatedPayableDiscountMeta);
            } else {
              payload.appliedDiscountMetadata.splice(
                discountIndex,
                1,
                updatedPayableDiscountMeta,
              );
            }
          });

        // Clear discountDate for payables excluded from discount processing so
        // stale sentinel dates (e.g. 0001-01-01) are not rendered in the UI.
        paymentInformation.payment.payables
          .filter((payable) => !this.payableFilter(payable))
          .forEach((payable: PaymentPreviewPayableV1Response) => {
            const documentIndex = payload.selectedDocuments.findIndex(
              (x) => x.urn === payable.burn,
            );
            if (documentIndex !== -1) {
              payload.selectedDocuments[documentIndex] = {
                ...payload.selectedDocuments[documentIndex],
                discountDate: null,
              };
            }
          });
      },
    );

    return payload;
  }

  private payableFilter(payable: PaymentPreviewPayableV1Response) {
    const result =
      !payable.discountExclusionReasons?.includes(
        DiscountExclusionReason.NotEnoughDiscountInfo.toString(),
      ) &&
      payable.availableDiscount !== 0 &&
      (!payable.discountExpirationDate ||
        new Date(payable.discountExpirationDate).getUTCFullYear() !== 1);

    return result;
  }

  getPayableDiscountState(
    payable: PaymentPreviewPayableV1Response,
  ): SessionDocumentDiscountState {
    const discountState = this.getDiscountState(payable);

    if (payable.isDiscountApplied) {
      return discountState.Applied;
    }

    return this.getDiscountReason(payable, discountState);
  }

  private getDiscountState(payable: PaymentPreviewPayableV1Response) {
    // workaround for subdued icon colors; see BTPAY-3376
    const dark = 'rgba(0, 24, 33, 0.7)';
    const warning = 'rgba(252, 167, 0, 0.7)';
    const success = 'rgba(61, 169, 70, 0.7)';
    const discountStateStyle = 'vertical-align: text-bottom;';
    const discountState: DiscountState = {
      DiscountExpired: {
        hint: this.translationService.instant(
          'ARC_PAYMENT_DISCOUNT_EXPIRED',
        ) as string,
        icon: 'cancel',
        color: dark,
        style: discountStateStyle,
        displayedDiscountAmount: payable.availableDiscount ?? 0,
      } as SessionDocumentDiscountState,
      PaymentMethodNotDiscountable: {
        hint: this.translationService.instant(
          'ARC_PAYMENT_CHOOSE_ACH_APPLY_DISCOUNT',
        ) as string,
        icon: 'info_outline',
        color: warning,
        style: discountStateStyle,
        displayedDiscountAmount: payable.availableDiscount ?? 0,
      },
      Applied: {
        hint: this.translationService.instant(
          'ARC_PAYMENT_DISCOUNT_APPLIED',
        ) as string,
        icon: 'check_circle_outline',
        color: success,
        style: discountStateStyle,
        displayedDiscountAmount: payable.availableDiscount ?? 0,
      } as SessionDocumentDiscountState,
      NotPaidInFull: {
        hint: this.translationService.instant(
          'ARC_PAYMENT_DISCOUNT_NOT_PAID_IN_FULL',
        ) as string,
        icon: 'info_outline',
        color: warning,
        style: discountStateStyle,
        displayedDiscountAmount: 0, // The Not Paid In Full exclusion reason will display $0 as the discount amount. Other exclusion reasons will display the available discount.
      } as SessionDocumentDiscountState,
      ScheduledPaymentDateIsAfterDiscountExpiration: {
        hint: this.translationService.instant(
          'ARC_PAYMENT_SELECT_EARLIEST_DATE_APPLY_DISCOUNT',
        ) as string,
        icon: 'info_outline',
        color: warning,
        style: discountStateStyle,
        displayedDiscountAmount: payable.availableDiscount ?? 0,
      } as SessionDocumentDiscountState,
      SupplierUserOverride: {
        hint: this.translationService.instant(
          'ARC_PAYMENT_DISCOUNT_NOT_APPLIED',
        ) as string,
        icon: 'warning_amber',
        color: warning,
        style: discountStateStyle,
        displayedDiscountAmount: 0,
      } as SessionDocumentDiscountState,
    };

    return discountState;
  }

  private getDiscountReason(
    payable: PaymentPreviewPayableV1Response,
    discountState: DiscountState,
  ) {
    const exclusionReasons = payable.discountExclusionReasons || [];
    const reasons = Object.keys(DiscountExclusionReason);
    const reasonKey = reasons.find((r) =>
      exclusionReasons.some((er) => er === r),
    );
    return discountState[reasonKey ?? DiscountExclusionReason.DiscountExpired];
  }

  private buildDefaultAppliedDiscountMeta(
    payable: PaymentPreviewPayableV1Response,
    discountState: SessionDocumentDiscountState,
  ) {
    const defaultAppliedDiscountMeta: AppliedDiscountMeta = {
      urn: payable.burn,
      appliedDiscountAmount: payable.appliedDiscount ?? 0,
      discountAmount: payable.availableDiscount ?? 0,
      discountDate: payable.discountExpirationDate,
      isDiscountApplied: payable.isDiscountApplied,
      discountState,
      discountedPayableAmount: payable.amount,
    };

    return defaultAppliedDiscountMeta;
  }

  private updatePayableDocument(
    selectedDocuments: SessionDocumentInfo[],
    defaultAppliedDiscountMeta: AppliedDiscountMeta,
  ) {
    const updatedDocument: SessionDocumentInfo = selectedDocuments.find(
      (x) => x.urn === defaultAppliedDiscountMeta.urn,
    );

    const paymentAmountIncludesDiscount =
      defaultAppliedDiscountMeta.isDiscountApplied &&
      updatedDocument.originalPayableAmount ===
        roundTo2Decimals(
          updatedDocument.openBalance -
            defaultAppliedDiscountMeta.appliedDiscountAmount,
        );

    updatedDocument.originalSessionPayableAmount = paymentAmountIncludesDiscount
      ? updatedDocument.openBalance
      : updatedDocument.originalSessionPayableAmount;

    updatedDocument.originalPayableAmount =
      defaultAppliedDiscountMeta.appliedDiscountAmount
        ? roundTo2Decimals(
            updatedDocument.originalSessionPayableAmount -
              defaultAppliedDiscountMeta.appliedDiscountAmount,
          )
        : updatedDocument.originalSessionPayableAmount;

    updatedDocument.payableAmount =
      defaultAppliedDiscountMeta.discountedPayableAmount;
    updatedDocument.discountAmount = defaultAppliedDiscountMeta.discountAmount;
    updatedDocument.discountDate = defaultAppliedDiscountMeta.discountDate;
    updatedDocument.appliedDiscountAmount =
      defaultAppliedDiscountMeta.appliedDiscountAmount;

    return updatedDocument;
  }

  private updatePayableDiscountMeta(
    appliedDiscountMeta: AppliedDiscountMeta[],
    defaultAppliedDiscountMeta: AppliedDiscountMeta,
  ) {
    const updatedDiscountMeta: AppliedDiscountMeta = appliedDiscountMeta?.find(
      (x) => x.urn === defaultAppliedDiscountMeta.urn,
    ) ?? {
      ...defaultAppliedDiscountMeta,
    };

    updatedDiscountMeta.discountState =
      defaultAppliedDiscountMeta.discountState;

    return updatedDiscountMeta;
  }
}
